import { useEffect } from 'react';
import { useAppSelector } from './redux';
import type { RootState } from '@/store';

/**
 * Associates the Umami analytics session with the logged-in user.
 *
 * Sends only the internal user id plus non-PII segmentation dimensions
 * (role, locale, status) — never email or name — per Umami's guidance for
 * identifying logged-in users.
 *
 * Because the Umami script is loaded with `defer`, `window.umami` may not
 * exist yet when the user first becomes available, so we poll briefly until
 * it is ready before calling identify.
 */
export function useIdentifyUser(): void {
  const user = useAppSelector((state: RootState) => state.auth.user);

  useEffect(() => {
    if (!user) return;

    const payload: UmamiIdentifyData = {
      id: String(user.id),
      role: user.role,
      locale: user.locale ?? 'unknown',
      status: user.status,
    };

    let attempts = 0;
    const maxAttempts = 40; // ~10s at 250ms intervals
    const timer = window.setInterval(() => {
      attempts += 1;
      if (window.umami?.identify) {
        window.umami.identify(payload);
        window.clearInterval(timer);
      } else if (attempts >= maxAttempts) {
        window.clearInterval(timer);
      }
    }, 250);

    return () => window.clearInterval(timer);
  }, [user]);
}
